import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { NavigateOptions, To } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import config from '../envConfig';

const SESSION_STACK_KEY = 'sg_mobile_nav_stack_v1';

function readStackFromSession(): string[] {
  try {
    const raw = sessionStorage.getItem(SESSION_STACK_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeStackToSession(stack: string[]) {
  try {
    sessionStorage.setItem(SESSION_STACK_KEY, JSON.stringify(stack));
  } catch (e) {
    console.error(e);
  }
}

function currentLocationKey(loc: {
  pathname: string;
  search: string;
  hash: string;
}) {
  return `${loc.pathname}${loc.search}${loc.hash}`;
}

export interface INavigationStackActions {
  navigate: (to: To, options?: NavigateOptions) => void;
  back: () => void;
  replace: (to: To, options?: Omit<NavigateOptions, 'replace'>) => void;
  replaceFull: (to: To, options?: Omit<NavigateOptions, 'replace'>) => void;
}

const NavigationStackContext = createContext<
  INavigationStackActions | undefined
>(undefined);

export function NavigationStackProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const reactNavigate = useNavigate();
  const location = useLocation();
  const [stack, setStack] = useState<string[]>(() => {
    if (!config.mobileRouting) {
      return [];
    }
    const path = currentLocationKey({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
    const saved = readStackFromSession();
    if (saved.length === 0) {
      return [path];
    }
    if (saved[saved.length - 1] === path) {
      return saved;
    }
    return [path];
  });

  const stackRef = useRef(stack);
  stackRef.current = stack;

  useEffect(() => {
    if (!config.mobileRouting) {
      return;
    }
    writeStackToSession(stack);
  }, [stack]);

  const navigate = useCallback(
    (to: To, options?: NavigateOptions) => {
      if (!config.mobileRouting) {
        reactNavigate(to, options);
        return;
      }
      if (typeof to !== 'string') {
        reactNavigate(to, options);
        return;
      }
      const replace = options?.replace === true;
      if (replace) {
        const prev = stackRef.current;
        const next = prev.length === 0 ? [to] : [...prev.slice(0, -1), to];
        stackRef.current = next;
        setStack(next);
      } else {
        const next = [...stackRef.current, to];
        stackRef.current = next;
        setStack(next);
      }
      reactNavigate(to, options);
    },
    [reactNavigate],
  );

  const replace = useCallback(
    (to: To, options?: Omit<NavigateOptions, 'replace'>) => {
      navigate(to, { ...options, replace: true });
    },
    [navigate],
  );

  const replaceFull = useCallback(
    (to: To, options?: Omit<NavigateOptions, 'replace'>) => {
      if (!config.mobileRouting) {
        reactNavigate(to, { ...options, replace: true });
        return;
      }
      if (typeof to !== 'string') {
        reactNavigate(to, { ...options, replace: true });
        return;
      }
      stackRef.current = [to];
      setStack([to]);
      reactNavigate(to, { ...options, replace: true });
    },
    [reactNavigate],
  );

  const back = useCallback(() => {
    if (!config.mobileRouting) {
      reactNavigate(-1);
      return;
    }
    const prev = stackRef.current;
    if (prev.length <= 1) {
      return;
    }
    const next = prev.slice(0, -1);
    const target = next[next.length - 1];
    stackRef.current = next;
    setStack(next);
    reactNavigate(target, { replace: true });
  }, [reactNavigate]);

  const value = useMemo(
    () => ({ navigate, back, replace, replaceFull }),
    [navigate, back, replace, replaceFull],
  );

  useEffect(() => {
    if (!config.mobileRouting) {
      return;
    }
    const path = currentLocationKey(location);
    const top = stackRef.current[stackRef.current.length - 1];
    if (top === path) {
      return;
    }
    const idx = stackRef.current.lastIndexOf(path);
    if (idx >= 0) {
      const next = stackRef.current.slice(0, idx + 1);
      stackRef.current = next;
      setStack(next);
      return;
    }
    const next = [...stackRef.current, path];
    stackRef.current = next;
    setStack(next);
  }, [location]);

  return (
    <NavigationStackContext.Provider value={value}>
      {children}
    </NavigationStackContext.Provider>
  );
}

export function useNavigationStack(): INavigationStackActions {
  const ctx = useContext(NavigationStackContext);
  if (!ctx) {
    throw new Error(
      'useNavigationStack must be used within NavigationStackProvider',
    );
  }
  return ctx;
}
